// FastSpring 支付返回页面
// 支付完成后 FastSpring 会跳转到这里
// 显示结果并自动关闭

import React, { useEffect, useState } from 'react';
import { Result, Button, Card, Spin, Alert } from 'antd';

const PaymentReturn = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending'); // pending, success, cancelled, failed
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('fastspring_success');
    const cancel = params.get('fastspring_cancel');
    const error = params.get('fastspring_error');
    const orderNo = params.get('orderNo');

    console.log('Payment return params:', {
      success,
      cancel,
      error,
      orderNo,
      fullUrl: window.location.href
    });

    // 如果有订单号，轮询检查订单状态
    if (orderNo) {
      checkOrderStatus(orderNo);
    } else if (success === 'true') {
      // FastSpring 返回成功但没有订单号，假设成功
      setStatus('success');
      setLoading(false);
    } else if (cancel === 'true' || cancel === '') {
      setStatus('cancelled');
      setLoading(false);
    } else if (error) {
      setStatus('failed');
      setLoading(false);
    } else {
      // 没有足够信息判断状态
      setStatus('unknown');
      setLoading(false);
    }

    // 10 秒后自动关闭窗口
    const closeTimer = setTimeout(() => {
      try {
        // 如果是 window.open 打开的，可以尝试关闭
        if (window.opener && !window.opener.closed) {
          window.opener.focus();
        }
      } catch (e) {
        console.log('Cannot close window:', e);
      }
    }, 10000);

    return () => clearTimeout(closeTimer);
  }, []);

  // 轮询订单状态
  const checkOrderStatus = async (orderNo) => {
    let attempts = 0;
    const maxAttempts = 15; // 最多 15 次检查（约 30 秒）

    const poll = async () => {
      attempts++;
      console.log(`Checking order status... attempt ${attempts}/${maxAttempts}`);

      try {
        const res = await fetch(`/api/fastspring/order-status?orderNo=${orderNo}`);
        const data = await res.json();

        console.log('Order status response:', data);

        if (data.code === 0 && data.data) {
          setOrderInfo(data.data);
          
          if (data.data.status === 'succeeded') {
            setStatus('success');
            setLoading(false);
            return;
          }
          
          if (data.data.status === 'failed') {
            setStatus('failed');
            setLoading(false);
            return;
          }
        }

        // 如果取消或关闭
        const params = new URLSearchParams(window.location.search);
        if (params.get('fastspring_cancel') === '') {
          setStatus('cancelled');
          setLoading(false);
          return;
        }

        // 还没完成，继续轮询
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          // 超时，假设用户放弃了
          setStatus('timeout');
          setLoading(false);
        }

      } catch (error) {
        console.error('Error checking order status:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('error');
          setLoading(false);
        }
      }
    };

    poll();
  };

  const handleClose = () => {
    try {
      if (window.opener && !window.opener.closed) {
        // 刷新父窗口并关闭当前窗口
        window.opener.location.reload();
        window.close();
      } else {
        window.close();
      }
    } catch (e) {
      window.close();
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5'
      }}>
        <Card style={{ textAlign: 'center', minWidth: 300 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Processing payment...</p>
          <p style={{ color: '#999', fontSize: 12 }}>
            Checking payment status...
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f0f2f5',
      padding: 20
    }}>
      <Card style={{ maxWidth: 500, textAlign: 'center' }}>
        {status === 'success' && (
          <Result
            status="success"
            title="Payment Successful!"
            subTitle={
              <div>
                <p>Your payment has been processed successfully.</p>
                {orderInfo && (
                  <p style={{ marginTop: 8 }}>
                    <strong>Order:</strong> {orderInfo.order_no}<br />
                    <strong>Amount:</strong> ¥{orderInfo.amount?.toFixed(2)}
                  </p>
                )}
              </div>
            }
            extra={[
              <Button type="primary" key="close" onClick={handleClose}>
                Close Window
              </Button>,
              <Button key="dashboard" onClick={() => {
                if (window.opener) {
                  window.opener.location.href = '/';
                } else {
                  window.location.href = '/';
                }
              }}>
                Go to Dashboard
              </Button>
            ]}
          />
        )}

        {status === 'cancelled' && (
          <Result
            status="warning"
            title="Payment Cancelled"
            subTitle="Your payment was cancelled. No charges have been made."
            extra={[
              <Button type="primary" key="close" onClick={handleClose}>
                Close Window
              </Button>,
              <Button key="retry" onClick={() => {
                if (window.opener) {
                  window.opener.location.reload();
                  window.close();
                } else {
                  window.history.back();
                }
              }}>
                Try Again
              </Button>
            ]}
          />
        )}

        {status === 'failed' && (
          <Result
            status="error"
            title="Payment Failed"
            subTitle="Your payment could not be processed. Please try again or contact support."
            extra={[
              <Button type="primary" key="close" onClick={handleClose}>
                Close Window
              </Button>,
              <Button key="retry" onClick={() => {
                if (window.opener) {
                  window.opener.location.reload();
                  window.close();
                } else {
                  window.history.back();
                }
              }}>
                Try Again
              </Button>
            ]}
          />
        )}

        {(status === 'timeout' || status === 'unknown' || status === 'error') && (
          <>
            <Alert
              message="Payment Status Unknown"
              description="We couldn't confirm your payment status. Please check your orders page or contact support if you completed the payment."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            {orderInfo && (
              <p style={{ marginBottom: 16 }}>
                Order: {orderInfo.order_no}<br />
                Current Status: {orderInfo.status}
              </p>
            )}
            <Button type="primary" onClick={handleClose}>
              Close Window
            </Button>
          </>
        )}

        <p style={{ color: '#999', marginTop: 24, fontSize: 12 }}>
          This window can be safely closed.
        </p>
      </Card>
    </div>
  );
};

export default PaymentReturn;
